/**
 * ChangePasswordPage - Troca obrigatória de senha
 *
 * Quando o suporte gera uma senha temporária, a API marca
 * app_metadata.must_change_password e bloqueia as demais rotas (403
 * must_change_password). ProtectedRoute manda o usuário para cá a partir de
 * qualquer rota protegida, com ?redirect=<rota pretendida>.
 *
 * Tela sem sidebar e sem links para fora — só o formulário e "Sair". Depois
 * da troca, a API devolve uma sessão nova sem a flag; atualizamos o status e
 * seguimos para o redirect.
 */

import React, { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { useTranslation } from '@/contexts/LanguageContext';
import CoreStudioLogo from '@/components/ui/core-studio-logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import PasswordChangeForm from '@/components/auth/PasswordChangeForm';
import { CheckCircle2, LogOut, ShieldAlert } from 'lucide-react';

/** Só caminhos internos (evita open redirect) e nunca de volta para cá. */
function safeRedirect(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/change-password')) return '/';
  return raw;
}

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { refreshStatus, passwordChangePending } = useUserStatus();
  const [success, setSuccess] = useState(false);

  const redirectTo = safeRedirect(searchParams.get('redirect'));

  const handleSuccess = async () => {
    setSuccess(true);
    // Status é a fonte autoritativa da flag: rebuscar libera o ProtectedRoute.
    await refreshStatus();
    setTimeout(() => navigate(redirectTo, { replace: true }), 1500);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // Nada pendente (URL aberta à toa ou flag já limpa): segue para o destino.
  if (!passwordChangePending && !success) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-center">
          {/* Gradiente da marca só funciona sobre fundo escuro; no tema claro, monocromático */}
          <CoreStudioLogo width={200} className="hidden dark:block" />
          <CoreStudioLogo width={200} useCurrentColor className="text-foreground dark:hidden" />
        </div>

        <Card className="border-border/50 shadow-xl">
          {success ? (
            <CardHeader className="text-center pb-6">
              <div className="flex justify-center mb-3">
                <CheckCircle2 className="h-12 w-12 text-green-500" />
              </div>
              <CardTitle className="text-2xl font-bold">{t('changePassword.successTitle')}</CardTitle>
              <CardDescription>{t('changePassword.success')}</CardDescription>
            </CardHeader>
          ) : (
            <>
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-3">
                  <ShieldAlert className="h-10 w-10 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">{t('changePassword.title')}</CardTitle>
                <CardDescription>{t('changePassword.description')}</CardDescription>
                {user?.email && (
                  <p className="text-xs text-muted-foreground pt-1">{user.email}</p>
                )}
              </CardHeader>

              <CardContent className="space-y-4">
                <PasswordChangeForm strongPolicy onSuccess={handleSuccess} />
                <Button type="button" variant="ghost" className="w-full" onClick={handleSignOut}>
                  <LogOut className="h-4 w-4 mr-2" />
                  {t('changePassword.signOut')}
                </Button>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
